import { env } from "@postdaily/env/web";

/**
 * Cloudflare Turnstile site key. Public by design — it is embedded in the page
 * HTML. When unset the contact form renders no challenge; the server mirrors
 * that by skipping verification outside production (see routers/contact.ts).
 */
export const turnstileSiteKey = env.VITE_TURNSTILE_SITE_KEY;
export const turnstileEnabled = Boolean(turnstileSiteKey);

export type TurnstileRenderOptions = {
  action?: string;
  callback?: (token: string) => void;
  "error-callback"?: (code?: string) => void;
  "expired-callback"?: () => void;
  sitekey: string;
  size?: "compact" | "flexible" | "normal";
  theme?: "auto" | "dark" | "light";
};

export type TurnstileApi = {
  getResponse: (widgetId?: string) => string | undefined;
  isExpired: (widgetId?: string) => boolean;
  remove: (widgetId?: string) => void;
  render: (
    container: HTMLElement | string,
    options: TurnstileRenderOptions,
  ) => string | undefined;
  reset: (widgetId?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    __postdailyTurnstileOnload?: () => void;
  }
}

const SCRIPT_ID = "cf-turnstile-api";
const ONLOAD_CALLBACK = "__postdailyTurnstileOnload";
/**
 * Explicit rendering (rather than the implicit `cf-turnstile` class scan) — the
 * form is a client-rendered React subtree on a prerendered page, so the widget
 * has to be mounted on our schedule, not at script-parse time.
 */
const SCRIPT_SRC = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${ONLOAD_CALLBACK}`;

let loader: Promise<TurnstileApi> | null = null;

/**
 * Injects the Turnstile API script once per document and resolves the global
 * API. Memoized so concurrent widgets share a single script tag; a load failure
 * clears the memo so a later mount can retry.
 */
export function loadTurnstile(): Promise<TurnstileApi> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Turnstile is browser-only"));
  }

  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }

  if (loader) {
    return loader;
  }

  loader = new Promise<TurnstileApi>((resolve, reject) => {
    window[ONLOAD_CALLBACK] = () => {
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        loader = null;
        reject(new Error("Turnstile script loaded without an API"));
      }
    };

    // A script tag already in flight will call the onload hook we just
    // (re)assigned, so there is nothing left to do.
    if (document.getElementById(SCRIPT_ID)) {
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.defer = true;
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.onerror = () => {
      loader = null;
      script.remove();
      reject(new Error("Failed to load Turnstile"));
    };

    document.head.appendChild(script);
  });

  return loader;
}
